import { ref, computed, readonly } from "vue";
import { useGetAllAdminUsersQuery, useGetAdminUserQuery, useGetAdminUserRolesQuery } from "@/api/queries/admin/users";

// Global state for users
const selectedUserId = ref("");
const searchTerm = ref("");
const showDisabledUsers = ref(false);
const selectedUserRole = ref("");

// State management functions
export function setSelectedUserId(id: string) {
  selectedUserId.value = id;
}

export function setSearchTerm(term: string) {
  searchTerm.value = term;
}

export function toggleDisabledUsers() {
  showDisabledUsers.value = !showDisabledUsers.value;
}

export function setShowDisabledUsers(value: boolean) {
  showDisabledUsers.value = value;
}

export function setSelectedUserRole(roleId: string) {
  selectedUserRole.value = roleId;
}

// Computed state
export function useAdminUsersState() {
  const usersQuery = useGetAllAdminUsersQuery();
  const selectedUserQuery = useGetAdminUserQuery(selectedUserId.value);
  const userRolesQuery = useGetAdminUserRolesQuery();

  const allUsers = computed(() => {
    if (usersQuery.isError.value) return [];
    return usersQuery.data.value?.users || [];
  });

  const selectedUser = computed(() => {
    if (selectedUserQuery.isError.value) return undefined;
    return selectedUserQuery.data.value?.user;
  });

  const userRoles = computed(() => {
    if (userRolesQuery.isError.value) return [];
    return userRolesQuery.data.value?.roles || [];
  });

  const filteredUsers = computed(() => {
    let users = allUsers.value;

    if (showDisabledUsers.value) {
      users = users.filter((user: any) => user.status === 0);
    } else {
      users = users.filter((user: any) => user.status === 1);
    }

    if (searchTerm.value) {
      const searchLower = searchTerm.value.toLowerCase();
      users = users.filter(
        (user: any) =>
          user.email.toLowerCase().includes(searchLower) ||
          user.first_name.toLowerCase().includes(searchLower) ||
          user.last_name.toLowerCase().includes(searchLower) ||
          user.customers?.some((customer: any) => customer.name.toLowerCase().includes(searchLower)),
      );
    }

    return users;
  });

  return {
    // State
    selectedUserId: readonly(selectedUserId),
    searchTerm: readonly(searchTerm),
    showDisabledUsers: readonly(showDisabledUsers),
    selectedUserRole: readonly(selectedUserRole),

    // Computed
    allUsers,
    selectedUser,
    filteredUsers,
    userRoles,
    isLoading: computed(() => usersQuery.isLoading.value),
    isError: computed(() => usersQuery.error.value),
    error: computed(() => usersQuery.error.value),

    // Actions
    setSelectedUserId,
    setSearchTerm,
    toggleDisabledUsers,
    setShowDisabledUsers,
    setSelectedUserRole,
  };
}
